import * as React from "react";
import {
  ContractorReport,
  ContractorReportLineItem,
} from "@interfaces/contractorReport";
import { decodeReport, encodeReport } from "decoders/contractorReport";
import { getApiData, listApiData, postApiData } from "@hooks/utils/api";

export const useReport = (projectId?: string) => {
  const [reports, setReports] = React.useState<ContractorReportLineItem[]>([]);
  const [isReportLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getReport = async (
    id: string
  ): Promise<ContractorReportLineItem | undefined> => {
    try {
      setIsLoading(true);
      const reportResult = await getApiData(
        "getContractorReport",
        "report",
        id
      );
      if (Object.keys(reportResult.data).length === 0) {
        return undefined;
      }
      return reportResult
        ? decodeReport(reportResult.data as ContractorReport)
        : undefined;
    } catch (e: any) {
      setError("Could not fetch report");
    } finally {
      setIsLoading(false);
    }
  };

  const listReports = async (projectId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const reportResult = await listApiData(
        "listContractorReports",
        "report",
        projectId
      );
      const reportLineItems = await Promise.all(
        reportResult.data.map((p) => decodeReport(p as ContractorReport))
      );
      setReports(reportLineItems);
    } catch (e: any) {
      setError("Could not list reports");
    } finally {
      setIsLoading(false);
    }
  };

  const searchReports = async (
    query: string
  ): Promise<ContractorReportLineItem[]> => {
    try {
      const reportResult = await listApiData("searchReports", "report", query);
      return await Promise.all(
        reportResult.data.map((p) => decodeReport(p as ContractorReport))
      );
    } catch (e: any) {
      setError("Could not search reports");
      return [];
    }
  };
  const createReport = async (
    report: ContractorReportLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedReport = encodeReport(report);
      await postApiData("createContractorReport", "report", encodedReport);
    } catch (e: any) {
      setError("Could not update report");
    } finally {
      setIsLoading(false);
    }
  };

  const updateReport = async (
    report: ContractorReportLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedReport = encodeReport(report);
      await postApiData("updateContractorReport", "report", encodedReport);
      setReports(
        reports.map((c) => {
          if (c.id === report.id) {
            return report;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update report");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteReport = async (
    report: ContractorReportLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedReport = encodeReport(report);
      await postApiData("deleteReport", "report", encodedReport);
      setReports(reports.filter((p) => p.id !== report.id));
    } catch (e: any) {
      setError("Could not delete report");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (projectId) {
      listReports(projectId);
    }
  }, [projectId]);

  return {
    reports,
    getReport,
    listReports,
    createReport,
    updateReport,
    deleteReport,
    searchReports,
    isReportLoading,
    error,
  };
};
